import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { NVXPermission } from '@/lib/auth/roles';

// Re-export NVXPermission for backwards compatibility
export type { NVXPermission } from '@/lib/auth/roles';

/**
 * User information from authentication
 */
export interface AuthUser {
  /** Unique user identifier (subject claim) */
  sub: string;
  /** User email address */
  email: string;
  /** User display name */
  name: string;
  /** Organization name from Auth0 ID token claims */
  orgName?: string;
  /** Customer tenant code from Auth0 ID token claims */
  tenantCode?: string;
}

/**
 * Authentication state
 */
export interface AuthState {
  /** Access token for API calls */
  token: string | null;
  /** User information from ID token */
  user: AuthUser | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Loading state during initial auth check */
  isLoading: boolean;
  /** User permissions/roles for current tenant */
  permissions: NVXPermission[];
  /** Whether permissions have been fetched at least once for the current tenant */
  permissionsReady: boolean;
  /** Auth error message */
  error: string | null;
}

const initialState: AuthState = {
  token: null,
  user: null,
  isAuthenticated: false,
  isLoading: true,
  permissions: [],
  permissionsReady: false,
  error: null,
};

/**
 * Payload for setting credentials
 */
export interface SetCredentialsPayload {
  token: string;
  user: AuthUser;
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Set authentication credentials (token and user info)
     */
    setCredentials: (state, action: PayloadAction<SetCredentialsPayload>) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
    },

    /**
     * Update just the token (e.g., after silent refresh)
     */
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },

    /**
     * Update user information
     */
    setUser: (state, action: PayloadAction<AuthUser>) => {
      state.user = action.payload;
    },

    /**
     * Set user permissions for current tenant.
     */
    setPermissions: (state, action: PayloadAction<NVXPermission[]>) => {
      state.permissions = action.payload;
      state.permissionsReady = true;
    },

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Set auth error
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Clear authentication state (logout)
     */
    logout: (state) => {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.permissions = [];
      state.permissionsReady = false;
      state.error = null;
    },

    /**
     * Mark auth check as complete (not authenticated)
     */
    authCheckComplete: (state) => {
      state.isLoading = false;
    },
  },
});

export const {
  setCredentials,
  setToken,
  setUser,
  setPermissions,
  setLoading,
  setError,
  logout,
  authCheckComplete,
} = authSlice.actions;

// ============================================
// Selectors
// ============================================

/** Select the entire auth state */
export const selectAuth = (state: RootState) => state.auth;

/** Select if user is authenticated */
export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;

/** Select if auth is still loading */
export const selectIsAuthLoading = (state: RootState) => state.auth.isLoading;

/** Select current user */
export const selectUser = (state: RootState) => state.auth.user;

/** Select current token */
export const selectToken = (state: RootState) => state.auth.token;

/** Select user permissions */
export const selectPermissions = (state: RootState) => state.auth.permissions;

/** Select auth error */
export const selectAuthError = (state: RootState) => state.auth.error;

/** Select whether permissions have been fetched for the current tenant */
export const selectPermissionsReady = (state: RootState) =>
  state.auth.permissionsReady;

/**
 * Check if user has ANY of the specified permissions
 */
export const selectHasAnyPermission =
  (requiredPermissions: NVXPermission[]) => (state: RootState) => {
    const { permissions } = state.auth;
    return requiredPermissions.some((perm) => permissions.includes(perm));
  };

/**
 * Check if user has ALL of the specified permissions
 */
export const selectHasAllPermissions =
  (requiredPermissions: NVXPermission[]) => (state: RootState) => {
    const { permissions } = state.auth;
    return requiredPermissions.every((perm) => permissions.includes(perm));
  };

export default authSlice.reducer;
